<script>
  import { onMount } from "svelte"
  import { get } from "svelte/store"
  import GanttChartBox from "../../components/ganttchart/GanttChartBox.svelte"
  import { postStartEndData } from "../../utils/putData.js"
  import { usePageContext } from "../../components/pages/pageContext.js"
  import { FetchError, fetchResGanttProjectInfo, fetchGlobalProjectData } from "../../utils/getData.js";
  import Loading from "../../components/pages/Loading.svelte"
  import { batchUpdateGanttStore, initGanttStore } from "../../components/ganttchart/ganttStore/init.js";
  import { GanttChartTypes } from "../../components/ganttchart/ganttStore/enums.js";
  import { translate } from "../../i18n/i18next.js";
  import {
    mapProjectInfoToGanttStoreProperties
  } from "../../components/ganttchart/ganttStore/mapProjectInfoToGanttStoreProperties.js";
  import {
    connectResGanttConfigurationToGanttStore
  } from "../../views/generic/ResGantt/ResGanttConfigurationStorage.js";
  import { useProjectQuery } from "../../hooks/useProject.js";
  import { getFetchErrorHandlerContext } from "../../hooks/useFetchErrorHandlerContext.js";
  import {
    applyProjectAdminSettingsToGanttStore,
  } from "../../components/ganttchart/ganttStore/connectProjectAdminSettings.js";
  import { createResGanttFilters } from "../../views/generic/ResGantt/ResGanttFilters.js";
  import { connectSearchParamsToGanttStore } from "../../utils/searchParams/connectGanttStore.js";

  export let projectId
  export let user

  const resource_url = `/api/projects/${projectId}/ResGantt/res/`;
  const transaction_url = `/api/projects/${projectId}/transactions`;
  const metadata_url = `/api/projects/${projectId}/ResGantt/meta.json`;
  let projectInfo = null;

  // New GanttChart vars
  let projectData;
  let uploadTimeStamp;
  let downloadTimeStamp;
  let styleDict;
  let searchTables;
  let transactions;
  let readyToRender = false;

  const {setHeaderProps} = usePageContext()
  // const helpLink = "/knowledge/docs/frontend/views/res-gantt"
  $: setHeaderProps({
    title: projectInfo?.Project[5] ? `${projectInfo.Project[5]} (${translate("mySchedule.views.resGantt.name")})`: translate("mySchedule.views.resGantt.name"),
    uploadTime: uploadTimeStamp,
  })

  const GanttStore = initGanttStore()

  const {handleFetchErrors} = getFetchErrorHandlerContext()
  const projectQuery = useProjectQuery(projectId, {
    onSuccess: data => applyProjectAdminSettingsToGanttStore(GanttStore, data.settings),
  })
  // please keep the line below to force the above query to run
  // as I am yet to know how to do so on svelte-query declaratively...
  console.log($projectQuery)

  function fetchTransactionData(timestamp) {
    console.log("timestamp: " + timestamp);
    let _trUrl = transaction_url;
    if (timestamp)
      _trUrl += "?timestamp=" + timestamp;
    const xhr = new XMLHttpRequest();
    xhr.open("GET", _trUrl, false);
    xhr.send();

    if (xhr.status === 200) {
      return JSON.parse(xhr.responseText);
    }
    else {
      console.error("Failed to fetch transactions");
      return null;
    }
  }

  async function registerTransaction(operations) {
    // console.log("operations: ");
    // console.log(operations);
    let data = {};
    operations.forEach((ope) => {
      data[ope._operation] = {
        "Work_ResultStartTime": ope._resultStart,
        "Work_ResultEndTime": ope._resultEnd,
        "Work_Status": ope._resStatus,
        "Work_ResultQty": ope._resultQty,
        "Work_ResultProgress": ope._resultProgress,
        "Work_ResultObtainTime": ope._resultObtainTime
      };
    });

    return postStartEndData(projectId, JSON.stringify(data))
  }


  function fetchStyleInfo(styles) {
    const _styleDict = {};
    const _searchTables = {};

    styles.forEach((style) => {
      // Style
      const _styleUrl = `/api/projects/${projectId}/ResGantt/styles/${style.ObjectID}.json`;
      const xhr = new XMLHttpRequest();
      xhr.open("GET", _styleUrl, false);
      xhr.send();

      if (xhr.status === 200) {
        _styleDict[style.StyleName] = JSON.parse(xhr.responseText);
      }
      else {
        console.error("Failed to fetch style " + style);
      }

      // Search table
      const _tableUrl = `/api/projects/${projectId}/ResGantt/searchTables/${style.ObjectID}.json`;
      const xhrT = new XMLHttpRequest();
      xhrT.open("GET", _tableUrl, false);
      xhrT.send();

      if (xhrT.status === 200) {
        _searchTables[style.StyleName] = JSON.parse(xhrT.responseText);
        console.log("Search tables found for style " + style.StyleName);
      }
      else {
        console.error("Failed to fetch seatch table for style " + style.StyleName);
      }
    });

    transactions = fetchTransactionData(downloadTimeStamp);
    styleDict = _styleDict;
    searchTables = _searchTables;
  }

  onMount(async () => {
    try {
      projectInfo = await fetchResGanttProjectInfo(projectId);
      projectData = projectInfo.Project;
      uploadTimeStamp = projectInfo.UploadTimeStamp;
      downloadTimeStamp = projectInfo.LastDownloadTimeStamp;
      fetchStyleInfo(projectData[7]);

      batchUpdateGanttStore(GanttStore, {
        ...mapProjectInfoToGanttStoreProperties(projectInfo, await fetchGlobalProjectData(projectId)),
        GanttChartType: GanttChartTypes.ResGantt,
        ProjectId: projectId,
        UserId: user.id,
        StylesDict: styleDict,
        SearchTables: searchTables,
        RowDownloadUrl: resource_url,
        Transactions: transactions,
        TransactionsUrl: transaction_url,
        TransactionRegisteringFunction: registerTransaction,
        MetadataUrl: metadata_url
      })
      GanttStore.Filters.set(createResGanttFilters(get(GanttStore.RowsAllOwnedByStyle)))

      await connectResGanttConfigurationToGanttStore(GanttStore, user.id, projectId)

      await connectSearchParamsToGanttStore(GanttStore);

      readyToRender = true;
    } catch (e) {
      console.error(e);
      if (e instanceof FetchError) {
        handleFetchErrors(e.error)
      }
    }
  });
</script>

{#if readyToRender === true}
  <GanttChartBox
    GanttStore={GanttStore}
    boxDimensions="height: calc(100% - 5px);"
  />
{/if}
{#if projectInfo == null}
   <Loading/>
{/if}
